import { axios } from 'utils/axios';
import { useMutation, UseMutationResult } from '@tanstack/react-query';
import { queryClient } from 'utils/react-query';
import { ContentType, Like, LikedIds } from '@novaheal/types';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { ToastVariant } from 'types/Toast';

type MutationData = {
  id: string;
  type: ContentType;
};

type LikesCount = {
  articles: number;
  medications: number;
};

export const addLike = async ({ id, type }: MutationData): Promise<void> => {
  await axios.patch(`user/like/${id}`, { type });
};

export const useAddLike = (): UseMutationResult<void, unknown, MutationData, unknown> => {
  const { show } = useNovaToast();

  return useMutation({
    mutationFn: addLike,
    // If the request fails, onSuccess below never runs — the cache is
    // simply never patched, so the button stays in its pre-click state.
    // This just makes the failure visible instead of silently doing
    // nothing (mutateAsync's rejection is caught in useIsLiked).
    onError: () => {
      show({ variant: ToastVariant.ERROR });
    },
    // Patch the cached lists/counts directly instead of invalidating them —
    // avoids a full GET /user/likes (+ /user/likes/count) round trip on
    // every single like. The backend doesn't return the created row, but
    // nothing on the frontend reads a Like's own `id` (only contentfulId
    // and type are ever checked), so a locally-fabricated id is fine.
    onSuccess: (_data, { id, type }) => {
      queryClient.setQueryData<LikedIds>(['likes'], (current) => {
        if (!current) return current;

        const listKey = type === ContentType.ARTICLE ? 'likedArticles' : 'likedMedications';
        if (current[listKey].some((like) => like.contentfulId === id)) return current;

        const newLike: Like = { id: `optimistic-${id}`, contentfulId: id, type };
        return { ...current, [listKey]: [newLike, ...current[listKey]] };
      });

      queryClient.setQueryData<LikesCount>(['likes-count'], (current) => {
        if (!current) return current;
        return type === ContentType.ARTICLE
          ? { ...current, articles: current.articles + 1 }
          : { ...current, medications: current.medications + 1 };
      });

      queryClient.invalidateQueries({
        queryKey: ['liked-content'],
      });
      queryClient.invalidateQueries({
        queryKey: ['liked-content-paginated'],
      });
    },
  });
};
